import { z } from "zod/v4";

import {
  StripeProductVersionList,
  StripeSubscriptionIntervalList,
} from "../schemas/stripe";

import { StripeSubscriptionStatusList } from "./organization-subscriptions";
import { OrganizationDtoSchema } from "./organizations";
import {
  AdminStripeProductLinkDtoSchema,
  StripeProductLinkDtoSchema,
} from "./stripe-product-links";
import { SubscriptionBillingProviderList } from "./subscriptions";

export const StripeSubscriptionLinkDtoSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  stripeSubscriptionId: z.string().nullish(),
  shopifyAppSubscriptionId: z.string().nullish(),
  shopifyAppSubscriptionLineItemId: z.string().nullish(),
  autoScale: z.boolean(),
  isBalanceExceeded: z.boolean(),
  internalNotes: z.string().nullish(),
  version: z.enum(StripeProductVersionList),
  createdAtTs: z.number(),
  stripeProductLinks: StripeProductLinkDtoSchema.array(),
});

export type StripeSubscriptionLinkDto = z.infer<
  typeof StripeSubscriptionLinkDtoSchema
>;

export const UpcomingPriceChangeDtoSchema = z.object({
  priceId: z.string(),
  timestamp: z.number(),
});

export type UpcomingPriceChangeDto = z.infer<
  typeof UpcomingPriceChangeDtoSchema
>;

export const StripeSubscriptionSummaryDtoSchema =
  StripeSubscriptionLinkDtoSchema.extend({
    customer: z.object({ id: z.string(), billing: z.boolean() }),
    billingProvider: z.enum(SubscriptionBillingProviderList),
    agencyEmails: z.string().array().nullish(),
    status: z.enum(StripeSubscriptionStatusList),
    stripeSubscriptionScheduleId: z.string().nullable(),
    stripeProductLinks: AdminStripeProductLinkDtoSchema.array(),
    cancelAt: z.number().nullish(),
    interval: z.enum(StripeSubscriptionIntervalList),
    intervalCount: z.number().nullish(),
    coupon: z
      .object({
        amountOff: z.number().nullish(),
        percentOff: z.number().nullish(),
      })
      .nullish(),
    upcomingPrices: UpcomingPriceChangeDtoSchema.array(),
    endingPrices: UpcomingPriceChangeDtoSchema.array(),
    periodStart: z.number(),
    periodEnd: z.number(),
    stripePriceId: z.string(),
  });

export type StripeSubscriptionSummaryDto = z.infer<
  typeof StripeSubscriptionSummaryDtoSchema
>;

export const CreateStripeSubscriptionLinkDtoSchema =
  StripeSubscriptionLinkDtoSchema.extend({
    id: z.uuid().nullish(),
    organizationId: z.uuid(),
    createdAtTs: z.number().nullish(),
  }).omit({ stripeProductLinks: true });

export type CreateStripeSubscriptionLinkDto = z.infer<
  typeof CreateStripeSubscriptionLinkDtoSchema
>;

export const PatchStripeSubscriptionLinkDtoSchema =
  StripeSubscriptionLinkDtoSchema.pick({
    autoScale: true,
    internalNotes: true,
    shopifyAppSubscriptionId: true,
    shopifyAppSubscriptionLineItemId: true,
  }).partial();

export type PatchStripeSubscriptionLinkDto = z.infer<
  typeof PatchStripeSubscriptionLinkDtoSchema
>;

export const ActiveStripeSubscriptionDtoSchema = z.object({
  stripeSubscriptionId: z.string(),
  products: z
    .object({
      id: z.string(),
      name: z.string(),
      price: z.number(),
      interval: z.enum(StripeSubscriptionIntervalList),
    })
    .array(),
});

export type ActiveStripeSubscriptionDto = z.infer<
  typeof ActiveStripeSubscriptionDtoSchema
>;

export type AppCreditRow = {
  shopId: string;
  date: string;
  creditAmount: string;
};
